import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Department, Landmark } from "../types";
import { Search, ArrowRight, Navigation } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faElevator,
  faRestroom,
  faCoffee,
  faCouch,
  faCircleInfo,
  faLocationDot,
  faTruckMedical,
  faUserDoctor,
  faSyringe,
  faXRay,
  faPills,
  faBriefcaseMedical,
  faHeart,
  faBed,
  faLungs,
  faBaby,
  faMicroscope,
  faHospital,
  faStethoscope,
  faSquareParking,
  faMosque,
} from "@fortawesome/free-solid-svg-icons";

interface LandmarksPanelProps {
  landmarks: Landmark[];
  departments: Department[];
  onSelectLandmark: (landmark: Landmark) => void;
  onSelectDepartment: (department: Department) => void;
  onBack: () => void;
  initialCategory?: string;
  kioskData?: any;
}

// ── Icon + color helpers ──────────────────────────────────────────────────────

function getLandmarkMeta(name: string): {
  icon: any;
  color: string;
  bg: string;
} {
  const n = name.toLowerCase();
  if (n.includes("elevator") || n.includes("lift"))
    return { icon: faElevator, color: "#818CF8", bg: "rgba(129,140,248,0.15)" };
  if (n.includes("toilet") || n.includes("restroom") || n.includes("washroom"))
    return { icon: faRestroom, color: "#22D3EE", bg: "rgba(34,211,238,0.15)" };
  if (n.includes("escalator"))
    return { icon: faElevator, color: "#A78BFA", bg: "rgba(167,139,250,0.15)" };
  if (n.includes("cafe") || n.includes("coffee") || n.includes("costa"))
    return { icon: faCoffee, color: "#D97706", bg: "rgba(217,119,6,0.15)" };
  if (n.includes("parking"))
    return {
      icon: faSquareParking,
      color: "#3B82F6",
      bg: "rgba(59,130,246,0.15)",
    };
  if (n.includes("mosque") || n.includes("prayer"))
    return { icon: faMosque, color: "#10B981", bg: "rgba(16,185,129,0.15)" };
  if (n.includes("info") || n.includes("appointment"))
    return {
      icon: faCircleInfo,
      color: "#0EA5E9",
      bg: "rgba(14,165,233,0.15)",
    };
  if (n.includes("waiting"))
    return { icon: faCouch, color: "#EC4899", bg: "rgba(236,72,153,0.15)" };
  return {
    icon: faLocationDot,
    color: "#F87171",
    bg: "rgba(248,113,113,0.15)",
  };
}

function getDeptMeta(
  name: string,
  category?: string,
): { icon: any; color: string; bg: string } {
  const n = name.toLowerCase();
  const c = (category || "").toLowerCase();
  if (n.includes("respiratory") || n.includes("lung"))
    return { icon: faLungs, color: "#06B6D4", bg: "rgba(6,182,212,0.15)" };
  if (n.includes("pediatric") || n.includes("child") || n.includes("neonatal"))
    return { icon: faBaby, color: "#EC4899", bg: "rgba(236,72,153,0.15)" };
  if (n.includes("diagnostic") || n.includes("pathology"))
    return {
      icon: faMicroscope,
      color: "#8B5CF6",
      bg: "rgba(139,92,246,0.15)",
    };
  if (n.includes("icu") || n.includes("intensive") || c.includes("icu"))
    return { icon: faBed, color: "#F97316", bg: "rgba(249,115,22,0.15)" };
  if (n.includes("emergency") || c.includes("emergency"))
    return {
      icon: faTruckMedical,
      color: "#EF4444",
      bg: "rgba(239,68,68,0.15)",
    };
  if (n.includes("surgery") || n.includes("operating"))
    return {
      icon: faBriefcaseMedical,
      color: "#3B82F6",
      bg: "rgba(59,130,246,0.15)",
    };
  if (n.includes("cardio") || n.includes("heart"))
    return { icon: faHeart, color: "#EF4444", bg: "rgba(239,68,68,0.15)" };
  if (n.includes("lab") || c.includes("lab"))
    return { icon: faSyringe, color: "#14B8A6", bg: "rgba(20,184,166,0.15)" };
  if (n.includes("radio") || n.includes("imaging") || n.includes("x-ray"))
    return { icon: faXRay, color: "#A855F7", bg: "rgba(168,85,247,0.15)" };
  if (n.includes("pharmacy") || n.includes("drug"))
    return { icon: faPills, color: "#EC4899", bg: "rgba(236,72,153,0.15)" };
  if (n.includes("opd") || n.includes("outpatient") || n.includes("clinic"))
    return { icon: faUserDoctor, color: "#22C55E", bg: "rgba(34,197,94,0.15)" };
  if (n.includes("consultation") || n.includes("checkup"))
    return {
      icon: faStethoscope,
      color: "#0EA5E9",
      bg: "rgba(14,165,233,0.15)",
    };
  return { icon: faHospital, color: "#94A3B8", bg: "rgba(148,163,184,0.15)" };
}

// ── Sub-components ────────────────────────────────────────────────────────────

type Tab = "landmarks" | "departments";

const TABS: { id: Tab; label: string }[] = [
  { id: "landmarks", label: "Landmarks" },
  { id: "departments", label: "Departments" },
];

interface CardItem {
  id: string;
  name: string;
  subtitle?: string;
  icon: any;
  color: string;
  bg: string;
  floor?: string;
}

const PlaceCard: React.FC<{
  item: CardItem;
  onClick: () => void;
  index: number;
}> = ({ item, onClick, index }) => (
  <motion.button
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.03, duration: 0.25 }}
    whileTap={{ scale: 0.97 }}
    onClick={onClick}
    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl border text-left group transition-all"
    style={{
      backgroundColor: "var(--surface-3)",
      borderColor: "var(--surface-4)",
    }}
    onMouseEnter={(e) => {
      const el = e.currentTarget as HTMLElement;
      el.style.backgroundColor = "var(--surface-4)";
      el.style.borderColor = item.color;
    }}
    onMouseLeave={(e) => {
      const el = e.currentTarget as HTMLElement;
      el.style.backgroundColor = "var(--surface-3)";
      el.style.borderColor = "var(--surface-4)";
    }}
  >
    {/* Icon bubble */}
    <div
      className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
      style={{ backgroundColor: item.bg }}
    >
      <FontAwesomeIcon
        icon={item.icon}
        style={{ color: item.color }}
        size="lg"
      />
    </div>

    {/* Text */}
    <div className="flex-1 min-w-0">
      <p
        className="text-sm font-semibold leading-tight truncate"
        style={{ color: "var(--color-text-primary)" }}
      >
        {item.name}
      </p>
      {item.subtitle && (
        <p
          className="text-xs mt-0.5 truncate"
          style={{ color: "var(--color-text-muted)" }}
        >
          {item.subtitle}
        </p>
      )}
    </div>

    {/* Arrow */}
    <div
      className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
      style={{ backgroundColor: item.color + "22" }}
    >
      <ArrowRight size={14} style={{ color: item.color }} />
    </div>
  </motion.button>
);

// ── Main component ────────────────────────────────────────────────────────────

const LandmarksPanel: React.FC<LandmarksPanelProps> = ({
  landmarks,
  departments,
  onSelectLandmark,
  onSelectDepartment,
  initialCategory,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>("landmarks");
  const [query, setQuery] = useState("");

  const filteredDepartments = useMemo(() => {
    let list = departments;
    if (initialCategory && initialCategory !== "All")
      list = list.filter((d) => d.category === initialCategory);
    if (query)
      list = list.filter((d) =>
        d.name.toLowerCase().includes(query.toLowerCase()),
      );
    return list;
  }, [departments, initialCategory, query]);

  const filteredLandmarks = useMemo(() => {
    if (!query) return landmarks;
    return landmarks.filter((l) =>
      l.name.toLowerCase().includes(query.toLowerCase()),
    );
  }, [landmarks, query]);

  const landmarkCards: CardItem[] = filteredLandmarks.map((l) => {
    const meta = getLandmarkMeta(l.name);
    return {
      id: l.id,
      name: l.name,
      subtitle: l.description || "Landmark",
      ...meta,
    };
  });

  const deptCards: CardItem[] = filteredDepartments.map((d) => {
    const meta = getDeptMeta(d.name, d.category);
    return {
      id: d.id,
      name: d.name,
      subtitle: d.category || d.serviceType || "Department",
      ...meta,
    };
  });

  const activeCards = activeTab === "landmarks" ? landmarkCards : deptCards;
  const activeCount = activeCards.length;

  return (
    <div
      className="w-full h-full flex flex-col overflow-hidden"
      style={{ backgroundColor: "var(--surface-2)" }}
    >
      {/* ── Header ── */}
      <div
        className="px-4 pt-4 pb-3 border-b"
        style={{ borderColor: "var(--surface-3)" }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Navigation size={18} style={{ color: "#3B82F6" }} />
          <h2
            className="text-base font-bold"
            style={{ color: "var(--color-text-primary)" }}
          >
            Other Places
          </h2>
          <span
            className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full"
            style={{
              backgroundColor: "var(--surface-4)",
              color: "var(--color-text-secondary)",
            }}
          >
            {activeCount}
          </span>
        </div>

        {/* Search */}
        <div className="relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: "var(--color-text-muted)" }}
          />
          <input
            type="text"
            placeholder="Search places..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{
              backgroundColor: "var(--surface-3)",
              border: "1px solid var(--surface-4)",
              color: "var(--color-text-primary)",
            }}
          />
        </div>
      </div>

      {/* ── Tabs ── */}
      <div
        className="flex border-b"
        style={{ borderColor: "var(--surface-3)" }}
      >
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const count =
            tab.id === "landmarks"
              ? filteredLandmarks.length
              : filteredDepartments.length;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex-1 py-2.5 text-xs font-semibold transition-all relative"
              style={{
                color: isActive ? "#3B82F6" : "var(--color-text-muted)",
                backgroundColor: isActive ? "var(--surface-3)" : "transparent",
              }}
            >
              {tab.label}
              <span
                className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px]"
                style={{
                  backgroundColor: isActive ? "#3B82F620" : "var(--surface-4)",
                  color: isActive ? "#3B82F6" : "var(--color-text-muted)",
                }}
              >
                {count}
              </span>
              {isActive && (
                <motion.div
                  layoutId="tab-indicator"
                  className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
                  style={{ backgroundColor: "#3B82F6" }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* ── Card list ── */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: activeTab === "landmarks" ? -20 : 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="space-y-2"
          >
            {activeCards.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "var(--surface-3)" }}
                >
                  <Search
                    size={20}
                    style={{ color: "var(--color-text-muted)" }}
                  />
                </div>
                <p
                  className="text-sm"
                  style={{ color: "var(--color-text-muted)" }}
                >
                  No results for "{query}"
                </p>
              </div>
            ) : (
              activeCards.map((item, i) => (
                <PlaceCard
                  key={item.id}
                  item={item}
                  index={i}
                  onClick={() =>
                    activeTab === "landmarks"
                      ? onSelectLandmark(
                          landmarks.find((l) => l.id === item.id)!,
                        )
                      : onSelectDepartment(
                          departments.find((d) => d.id === item.id)!,
                        )
                  }
                />
              ))
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LandmarksPanel;

